import { createRouter, createWebHistory } from "@ionic/vue-router";
import {
  RouteRecordRaw,
  RouteMeta,
  RouteLocationNormalizedLoaded,
  Router,
  RouteLocationRaw,
  NavigationGuardNext,
} from "vue-router";
import { ref } from "vue";
import { useWorksheetCreationStore } from "@/modules/j.createWorksheets/stores/createWksStore";
import fetchPDFData from "@/modules/5.account/helpers/milageInfo";
import { useUserSurveyAnswerStore } from "@/modules/y.extra/stores/answerSurveyStore";
import { useWksDropdownStore } from "@/modules/a.worksheets/stores/wksDropdownStore";
import { userTokenStore } from "@/stores/tokenStore";

export interface routeMetaData extends RouteMeta {
  footerIndex?: number;
  kind?: Array<"me" | "mentee" | "classroom">;
  depth?: number | typeof Number.POSITIVE_INFINITY;
}
/*
  DEPTH of Pages:
  login: 0
  homepage: 1
  other footer btns: 2
  footer btns clicks -> current+1
*/
const tokenStore = userTokenStore();

type CustomRouteRecordSingleView = RouteRecordRaw & { meta?: routeMetaData };

type CustomRouteRecord = CustomRouteRecordSingleView;

const routes: Array<CustomRouteRecord> = [
  {
    path: "/login",
    name: "LoginPage",
    component: () => import("@/modules/1.signin/views/pages/StartApp.vue"),
    meta: { depth: 0, public: true },
  },
  { path: "/", redirect: "/login" },
  {
    path: "/home",
    name: "Home",
    component: () => import("@/views/HomePage.vue"),
    meta: { footerIndex: 0 },
  },
  // {
  //   path: "/loading",
  //   name: "LoadingPage",
  //   component: () => import("@/modules/0.startApp/views/pages/LoadingPage.vue"),
  // },
  {
    path: "/endApp",
    name: "EndPage",
    component: () => import("@/modules/0.startApp/views/pages/EndPage.vue"),
    meta: { public: true },
  },
  {
    path: "/homepage",
    name: "HomePage",
    component: () => import("@/modules/2.homepage/views/pages/HomePage.vue"),
    meta: { footerIndex: 0, depth: 1 },
  },
  {
    path: "/archive-homepage",
    name: "ArchiveHomePage",
    component: () => import("@/modules/2.homepage/views/pages/HomePage.vue"),
    meta: { footerIndex: 0, depth: 1 },
  },
  {
    path: "/learn",
    name: "LearnPage",
    component: () => import("@/modules/3.learn/views/pages/LearnPage.vue"),
    meta: { footerIndex: 1, depth: 2 },
  },
  {
    path: "/ebook-page",
    component: () => import("@/modules/3.learn/views/pages/EbookChapters.vue"),
    meta: { footerIndex: 4, kind: [], depth: 4 },
  },
  {
    path: "/excel-report-group",
    name: "ExcelReportGroup",
    component: () =>
      import("@/modules/3.learn/views/pages/ExcelReportGroup.vue"),
    meta: { footerIndex: 1, depth: 2 },
  },
  {
    path: "/user-account",
    name: "AccountPage",
    component: () => import("@/modules/5.account/views/pages/AccountPage.vue"),
    meta: { footerIndex: 4, kind: ["me", "mentee"], depth: 2 },
  },
  {
    path: "/archive-user-account",
    name: "ArchiveAccountPage",
    component: () => import("@/modules/5.account/views/pages/AccountPage.vue"),
    meta: { footerIndex: 4, kind: ["me", "mentee"], depth: 2 },
  },
  {
    path: "/user-account-info",
    name: "AccountPageInfo",
    component: () =>
      import("@/modules/5.account/views/pages/AccountInfoPage.vue"),
    meta: { footerIndex: 4, kind: ["me", "mentee"], depth: 3 },
  },
  {
    path: "/user-account-edit",
    name: "AccountPageEdit",
    component: () =>
      import("@/modules/5.account/views/pages/AccountEditPage.vue"),
    meta: { footerIndex: 4, kind: ["me", "mentee"], depth: 4 },
  },
  {
    path: "/user-account-country-language",
    name: "AccountPageCountryLanguage",
    component: () =>
      import("@/modules/5.account/views/pages/AccountCountryLanguagePage.vue"),
    meta: { footerIndex: 4, kind: ["me", "mentee"], depth: 3 },
  },
  {
    path: "/user-account-send-code",
    name: "AccountPageSendCode",
    component: () =>
      import("@/modules/5.account/views/pages/AccountSendCode.vue"),
    meta: {
      footerIndex: 4,
      kind: ["me", "mentee"],
      depth: Number.POSITIVE_INFINITY,
    },
  },
  {
    path: "/user-account-send-code-success",
    name: "AccountPageSendCodeSuccess",
    component: () =>
      import("@/modules/5.account/views/pages/AccountSendCodeSuccess.vue"),
    meta: {
      footerIndex: 4,
      kind: ["me", "mentee"],
      depth: Number.POSITIVE_INFINITY,
    },
  },
  {
    path: "/licenses",
    name: "Licenses",
    component: () =>
      import("@/modules/5.account/views/pages/AccountLicenses.vue"),
    meta: {
      footerIndex: 4,
      kind: ["me", "mentee"],
      depth: Number.POSITIVE_INFINITY,
    },
  },
  {
    path: "/terms-and-conditions",
    name: "TermsAndConditions",
    component: () =>
      import("@/modules/5.account/views/pages/AccountTermsAndConditions.vue"),
    meta: {
      footerIndex: 4,
      kind: ["me", "mentee"],
      depth: Number.POSITIVE_INFINITY,
    },
  },
  {
    path: "/privacy-policy",
    name: "PrivacyAndPolicy",
    component: () =>
      import("@/modules/5.account/views/pages/AccountPrivacyAndPolicy.vue"),
    meta: {
      footerIndex: 4,
      kind: ["me", "mentee"],
      depth: Number.POSITIVE_INFINITY,
    },
  },
  {
    path: "/user-account-notifications",
    name: "AccountUserNotifications",
    component: () =>
      import("@/modules/5.account/views/pages/AccountUserNotifications.vue"),
    meta: {
      footerIndex: 4,
      kind: ["me", "mentee"],
      depth: Number.POSITIVE_INFINITY,
    },
  },
  {
    path: "/badges",
    name: "BadgesPage",
    component: () => import("@/modules/4.badges/views/pages/BadgesPage.vue"),
    meta: { footerIndex: 2, depth: 2 },
  },
  {
    path: "/offline-mode",
    name: "LearnOfflinePage",
    component: () =>
      import(
        "@/modules/k.offlineMode/2.learn/views/pages/LearnOfflinePage.vue"
      ),
    meta: { footerIndex: 1, kind: ["me", "mentee"], depth: 2 },
  },
  //   {
  //     path: "/downloads",
  //     name: "DownloadPage",
  //     component: () =>
  //       import("@/modules/6.downloads/views/pages/DownloadsPage.vue"),
  //     meta: { depth: 2 },
  //   },
  {
    path: "/student-activities",
    name: "StudentActivities",
    component: () =>
      import("@/modules/6.downloads/views/pages/StudentActivities.vue"),
    meta: { footerIndex: 3, kind: ["me", "mentee"], depth: 2 },
  },

  {
    path: "/group-students",
    name: "StudentPage",
    component: () =>
      import("@/modules/7.students/views/pages/StudentsPage.vue"),
    meta: { footerIndex: 3, kind: ["classroom"], depth: 2 },
  },

  {
    path: "/studentsInfo",
    name: "StudentInfoPage",
    component: () =>
      import("@/modules/7.students/views/pages/StudentsInfoPage.vue"),
    meta: { footerIndex: 3, kind: ["classroom"], depth: 3 },
  },
  {
    path: "/grades",
    name: "GradesPageClass",
    component: () => import("@/modules/8.grades/views/pages/GradesPage.vue"),
    meta: { footerIndex: 2, kind: ["classroom"], depth: 2 },
  },
  {
    path: "/statistics",
    name: "GradesPage",
    component: () => import("@/modules/8.grades/views/pages/GradesPage.vue"),
    meta: { footerIndex: 1, kind: ["me", "mentee"], depth: 2 },
  },
  {
    path: "/info",
    name: "InfoPage",
    component: () => import("@/modules/9.info/views/pages/InfoPage.vue"),
    meta: { footerIndex: 4, kind: ["classroom"], depth: 2 },
  },
  {
    path: "/info-edit",
    name: "InfoEditPage",
    component: () => import("@/modules/9.info/views/pages/InfoEditPage.vue"),
    meta: { footerIndex: 4, kind: ["classroom"], depth: 3 },
  },

  {
    path: "/add-students-group",
    name: "AddStudentsList",
    component: () => import("@/modules/9.info/views/pages/AddStudentsList.vue"),
    meta: { footerIndex: 4, kind: ["classroom"], depth: 3 },
  },
  {
    path: "/manage-students-group",
    name: "ManageStudentsList",
    component: () =>
      import("@/modules/9.info/views/pages/ManageStudentsList.vue"),
    meta: { footerIndex: 4, kind: ["classroom"], depth: 3 },
  },

  // JR routes\

  {
    path: "/delete-my-account",
    name: "DeleteAccount",
    component: () =>
      import("@/modules/1.signin/views/modals/DeleteAccountModal.vue"),
    meta: { depth: 0 },
  },

  {
    path: "/delete-my-account-success",
    name: "DeleteAccountSuccess",
    component: () =>
      import("@/modules/1.signin/views/modals/SuccessAccountDeleted.vue"),
    meta: { depth: 0 },
  },

  {
    path: "/reset-password",
    name: "ResetPassword",
    component: () =>
      import("@/modules/1.signin/views/pages/PasswordForgot.vue"),
    meta: { depth: 0, public: true },
  },
  {
    path: "/reset-password-info",
    name: "ResetPasswordInfo",
    component: () =>
      import("@/modules/1.signin/views/pages/PasswordForgotAinfo.vue"),
    meta: { depth: 1, public: true },
  },
  {
    path: "/reset-password-code",
    name: "ResetPasswordCodeB",
    component: () =>
      import("@/modules/1.signin/views/pages/PasswordForgotBcode.vue"),
    meta: { depth: 2, public: true },
  },
  {
    path: "/update-password",
    name: "ResetPasswordCodeC",
    component: () =>
      import("@/modules/1.signin/views/pages/PasswordForgotCpass.vue"),
    meta: { depth: 3, public: true },
  },
  {
    path: "/update-password-success",
    name: "ResetPasswordCodeD",
    component: () =>
      import("@/modules/1.signin/views/pages/PasswordForgotDsuccess.vue"),
    meta: { depth: 4, public: true },
  },
  {
    path: "/worksheet-user-page",
    name: "WorksheetUserPage",
    component: () =>
      import("@/modules/a.worksheets/views/pages/WorksheetUserPage.vue"),
    meta: { depth: 3 },
  },
  {
    path: "/wks-solve",
    redirect: "/worksheet",
  },
  {
    path: "/worksheet",
    name: "WorksheetUserPage",
    component: () =>
      import("@/modules/a.worksheets/views/pages/WorksheetUserPage.vue"),
    meta: { depth: 3 },
  },
  {
    path: "/worksheet-take-photos-ios/:maxNumFiles?/:maxSizeMBytes?/:maxDuration?",
    name: "WorksheetTakePhotosIos",
    component: () =>
      import("@/modules/a.worksheets/views/pages/PhotoMediaPageIos.vue"),
    meta: { depth: 4 },
  },
  {
    path: "/worksheet-take-photos/:maxNumFiles?/:maxSizeMBytes?/:maxDuration?",
    name: "WorksheetTakePhotos",
    component: () =>
      import("@/modules/a.worksheets/views/pages/PhotoMediaPage.vue"),
    meta: { depth: 4 },
  },
  {
    path: "/text-editor",
    name: "WorksheetWriteText",
    component: () =>
      import("@/modules/a.worksheets/views/pages/TextEditorPage.vue"),
    meta: { depth: 4 },
  },
  {
    path: "/worksheet-take-videos",
    name: "worksheetTakeVideos",
    component: () =>
      import("@/modules/a.worksheets/views/pages/VideoMediaPage.vue"),
    meta: { depth: 4 },
  },
  {
    path: "/worksheet-take-audios",
    name: "worksheetRecordAudios",
    component: () =>
      import("@/modules/a.worksheets/views/pages/AudioMediaPage.vue"),
    meta: { depth: 4 },
  },
  {
    path: "/worksheet-upload-document",
    name: "worksheetUploadFiles",
    component: () =>
      import("@/modules/a.worksheets/views/pages/DocumentMediaPage.vue"),
    meta: { depth: 4 },
  },
  {
    path: "/worksheet-upload-pdf",
    name: "worksheetUploadDocuments",
    component: () =>
      import("@/modules/a.worksheets/views/pages/PdfMediaPage.vue"),
    meta: { depth: 4 },
  },
  {
    //TODO: activity complete -> worksheet route instead of worksheet-solve
    path: "/worksheet-solve",
    redirect: "worksheet",
  },
  {
    path: "/teacher-class-portfolio",
    component: () =>
      import("@/modules/a.worksheets/views/pages/WorksheetUserPage.vue"),
    beforeEnter: () => {
      const dd = useWksDropdownStore();
      dd.selectStudentUuid = "";
    },
    meta: { depth: 3 },
  },
  {
    path: "/worksheet-ebook",
    component: () =>
      import("@/modules/a.worksheets/views/pages/WorksheetUserEbook.vue"),
    meta: { depth: 4 },
  },
  {
    path: "/worksheet-ebook-class",
    component: () =>
      import("@/modules/a.worksheets/views/pages/WorksheetClassEbook.vue"),
    meta: { depth: 4, kind: ["classroom"] },
  },
  {
    path: "/create-account",
    component: () => import("@/modules/1.signin/views/pages/RegisterIPage.vue"),
    meta: { depth: 1, public: true },
  },
  {
    path: "/user-country-language",
    component: () =>
      import("@/modules/1.signin/views/pages/RegisterIIPage.vue"),
    meta: { depth: 2, public: true },
  },
  {
    path: "/nick-birth",
    component: () =>
      import("@/modules/1.signin/views/pages/RegisterIIIStuTeaPage.vue"),
    meta: { depth: 3, public: true },
  },
  {
    path: "/underage-parent-email",
    component: () =>
      import("@/modules/1.signin/views/pages/RegisterUnderAgeParentPage.vue"),
    meta: { depth: 4, public: true },
  },
  {
    path: "/choose-role",
    component: () =>
      import("@/modules/1.signin/views/pages/RegisterChooseRolePage.vue"),
    meta: { depth: 5, public: true },
  },
  {
    path: "/teacher-download-mooc",
    component: () =>
      import("@/modules/1.signin/views/pages/RegisterTeacherMooc.vue"),
    meta: { depth: 6, public: true },
  },
  {
    path: "/milage-info/:infoType",
    name: "milage-info",
    component: () => import("@/modules/5.account/views/pages/MilageInfo.vue"),
    props: (route) => ({
      infoType: String(route.params.infoType),
    }),
    meta: { depth: 7, public: true },
  },
  {
    path: "/create-account-success",
    component: () =>
      import("@/modules/1.signin/views/pages/RegisterSuccessPage.vue"),
    meta: { depth: 8, public: true },
  },
  {
    path: "/activation-code",
    component: () =>
      import("@/modules/1.signin/views/pages/ActivationCodePage.vue"),
    meta: { depth: 9, public: true },
  },
  {
    path: "/activation-info",
    component: () =>
      import("@/modules/1.signin/views/pages/ActivationInfo.vue"),
    meta: { public: true },
  },
  {
    path: "/activation-success",
    component: () =>
      import("@/modules/1.signin/views/pages/ActivationSuccessPage.vue"),
    meta: { depth: 10, public: true },
  },

  //-----
  //------jirodrig -------

  {
    path: "/go-worksheet/:uuid",
    name: "GoWorksheet",
    component: () => import("@/modules/3.learn/helpers/goWorksheet.vue"),
  },

  {
    path: "/geolocation",
    name: "GeolocationPage",
    component: () =>
      import("@/modules/d.notifications/views/pages/NotificationsPage.vue"),
  },
  {
    path: "/chat",
    name: "ChatPage",
    component: () =>
      import("@/modules/d.notifications/views/pages/NotificationsPage.vue"),
  },
  {
    path: "/notifications",
    name: "NotificationsPage",
    component: () =>
      import("@/modules/d.notifications/views/pages/NotificationsPage.vue"),
  },
  {
    path: "/ranks",
    name: "RankingsPage",
    component: () =>
      import("@/modules/e.rankings/views/pages/RankingsPage.vue"),
    meta: { footerIndex: 1 },
  },
  {
    path: "/courses",
    name: "CousesPage",
    component: () =>
      import("@/modules/d.notifications/views/pages/NotificationsPage.vue"),
    meta: { depth: 3 },
  },
  {
    path: "/classes",
    name: "ClassesPage",
    component: () =>
      import("@/modules/d.notifications/views/pages/NotificationsPage.vue"),
    meta: { depth: 3 },
  },
  {
    path: "/user-classes",
    name: "UserClasses",
    component: () => import("@/modules/f.courses/views/pages/ClassesPage.vue"),
    meta: { footerIndex: 4, kind: ["me", "mentee"], depth: 3 },
  },
  {
    path: "/archive",
    name: "ArchivePage",
    component: () => import("@/modules/h.archive/views/pages/ArchivePage.vue"),
  },
  {
    path: "/archive-learn",
    name: "ArchiveLearnPage",
    component: () =>
      import("@/modules/h.archive/views/pages/ArchiveLearnPage.vue"),
  },
  {
    path: "/search",
    name: "SearchPage",
    component: () =>
      import("@/modules/d.notifications/views/pages/NotificationsPage.vue"),
  },
  {
    path: "/statistics",
    name: "SearchPage",
    component: () =>
      import("@/modules/d.notifications/views/pages/NotificationsPage.vue"),
  },
  {
    path: "/user-courses",
    component: () => import("@/modules/f.courses/views/pages/CoursesPage.vue"),
    meta: { footerIndex: 4, kind: ["me", "mentee"], meta: { depth: 3 } },
  },
  {
    path: "/user-mentees",
    component: () => import("@/modules/g.mentees/views/MenteesPage.vue"),
    meta: { footerIndex: 4, kind: ["me", "mentee"], depth: 4 },
  },
  {
    path: "/create-new-class",
    component: () =>
      import("@/modules/i.createClassesCourses/views/pages/AddClassPage.vue"),
    meta: { footerIndex: 4, kind: ["me", "mentee"], depth: 4 },
  },

  {
    path: "/external-html",
    name: "ExternalHTMLPage",
    props: (route) => ({ src: route.query.src }),
    component: () => import("@/globals/pages/ExternalHTMLPage.vue"),
    meta: {},
  },
  {
    path: "/about",
    component: () =>
      import("@/modules/5.account/views/pages/AccountAboutPage.vue"),
    meta: { footerIndex: 4, kind: [], depth: 3 },
  },
  {
    path: "/create-worksheet",
    component: () =>
      import(
        "@/modules/j.createWorksheets/views/pages/CreateUpdateWorksheet.vue"
      ),
    beforeEnter: (
      to: RouteLocationNormalizedLoaded,
      from: RouteLocationNormalizedLoaded,
      next: NavigationGuardNext,
    ) => {
      // const createWksStore = useWorksheetCreationStore(pinia);
      const createWksStore = useWorksheetCreationStore();
      createWksStore.isNew = true;
      next();
    },
  },
  {
    path: "/create-update-problems",
    component: () =>
      import(
        "@/modules/j.createWorksheets/views/pages/CreateUpdateProblems.vue"
      ),
  },
  {
    path: "/create-update-worksheet",
    component: () =>
      import(
        "@/modules/j.createWorksheets/views/pages/CreateUpdateWorksheet.vue"
      ),
  },
  {
    path: "/download-wks",
    component: () =>
      import(
        "@/modules/k.offlineMode/0.learnDownloadWks/views/pages/DownloadWksPage.vue"
      ),
  },
  {
    path: "/wks-solve-offline",
    name: "WksSolveOffline",
    component: () =>
      import(
        "@/modules/k.offlineMode/1.worksheets/views/pages/WorksheetUserPageOffline.vue"
      ),
  },
  {
    path: "/upload-wks",
    component: () =>
      import(
        "@/modules/k.offlineMode/3.learnUpload/views/pages/LearnUploadPage.vue"
      ),
  },
  {
    path: "/worksheet-take-photos-offline",
    name: "WorksheetTakePhotosOffline",
    component: () =>
      import(
        "@/modules/k.offlineMode/1.worksheets/views/pages/PhotoMediaPageOffline.vue"
      ),
    meta: { depth: 4 },
  },
  {
    path: "/worksheet-take-photos-ios-offline",
    name: "WorksheetTakePhotosIosOffline",
    component: () =>
      import(
        "@/modules/k.offlineMode/1.worksheets/views/pages/PhotoMediaPageIosOffline.vue"
      ),
    meta: { depth: 4 },
  },
  {
    path: "/worksheet-take-videos-offline",
    name: "WorksheetTakeVideosOffline",
    component: () =>
      import(
        "@/modules/k.offlineMode/1.worksheets/views/pages/VideoMediaPageOffline.vue"
      ),
    meta: { depth: 4 },
  },
  {
    path: "/worksheet-take-audios-offline",
    name: "worksheetRecordAudiosOffline",
    component: () =>
      import(
        "@/modules/k.offlineMode/1.worksheets/views/pages/AudioMediaPageOffline.vue"
      ),
    meta: { depth: 4 },
  },
  {
    path: "/worksheet-upload-document-offline",
    name: "worksheetUploadFilesOffline",
    component: () =>
      import(
        "@/modules/k.offlineMode/1.worksheets/views/pages/DocumentMediaPageOffline.vue"
      ),
    meta: { depth: 4 },
  },
  {
    path: "/video-create",
    component: () =>
      import("@/modules/m.videorecorder/views/pages/RecordVideo.vue"),
  },
  {
    path: "/worksheet-upload-pdf-offline",
    name: "worksheetUploadDocumentsOffline",
    component: () =>
      import(
        "@/modules/k.offlineMode/1.worksheets/views/pages/PdfMediaPageOffline.vue"
      ),
    meta: { depth: 4 },
  },
  //COURSE_MANAGEMENT
  {
    path: "/course-management-info",
    component: () =>
      import(
        "@/modules/r.courseManagement/views/pages/CourseManagementInfo.vue"
      ),
  },

  {
    path: "/course-management-new",
    component: () =>
      import(
        "@/modules/r.courseManagement/views/pages/CourseManagementNewCourse.vue"
      ),
  },

  {
    path: "/course-management-edit",
    component: () =>
      import(
        "@/modules/r.courseManagement/views/pages/CourseManagementEdit.vue"
      ),
  },

  {
    path: "/course-management-curricula",
    component: () =>
      import(
        "@/modules/r.courseManagement/views/pages/CurriculaManagement.vue"
      ),
  },
  {
    path: "/temp",
    component: () => import("@/modules/t.temp/TempPage.vue"),
  },
  {
    path: "/translations",
    component: () =>
      import("@/modules/z.translations/views/pages/HandleTranslations.vue"),
    meta: { footerIndex: 4, kind: [], depth: 3 },
  },
  {
    path: "/worksheet-management",
    component: () =>
      import(
        "@/modules/u.worksheetsManagement/views/pages/WorksheetManagement.vue"
      ),
    meta: { footerIndex: 4, kind: [], depth: 3 },
  },
  {
    path: "/schools-management",
    component: () =>
      import("@/modules/v.schools/views/pages/SchoolsManagement.vue"),
    meta: { footerIndex: 4, kind: [], depth: 3 },
  },
  {
    path: "/school-create-edit",
    component: () =>
      import("@/modules/v.schools/views/pages/SchoolCreateEdit.vue"),
    meta: { footerIndex: 4, kind: [], depth: 4 },
  },
  {
    path: "/community-data",
    component: () =>
      import("@/modules/w.community/views/pages/CommunityPage.vue"),
    meta: { footerIndex: 4, kind: [], depth: 4 },
  },
  {
    path: "/reports-admin",
    component: () => import("@/modules/x.reports/views/pages/AdminReports.vue"),
    meta: { footerIndex: 4, kind: [], depth: 4 },
  },
  {
    path: "/competitions-management",
    component: () =>
      import("@/modules/e.rankings/views/pages/RankingsValidation.vue"),
    meta: { footerIndex: 4, kind: [], depth: 4 },
  },
  {
    path: "/national-ranking-students-validation",
    name: "NationalRankingStudentsValidation",
    component: () =>
      import(
        "@/modules/e.rankings/views/pages/NationalRankingsStudentsValidation.vue"
      ),
    meta: { footerIndex: 4, kind: [], depth: 5 },
  },
  {
    path: "/school-ranking-students-validation",
    name: "school-ranking-students-validation",
    component: () =>
      import(
        "@/modules/e.rankings/views/pages/SchoolRankingsStudentsValidation.vue"
      ),
    meta: { footerIndex: 4, kind: [], depth: 5 },
  },
  {
    path: "/schools-rankings-students-validation",
    name: "SchoolRankingStudentsValidation",
    component: () =>
      import(
        "@/modules/e.rankings/views/pages/SchoolRankingsStudentsValidation.vue"
      ),
    meta: { footerIndex: 4, kind: [], depth: 5 },
  },
  {
    path: "/admin-levels-years",
    name: "admin-levels-years",
    component: () =>
      import("@/modules/p.adminManagement/views/pages/AdminLevelYearsPage.vue"),
    meta: { footerIndex: 4, kind: [], depth: 5 },
  },
  {
    path: "/database-management",
    name: "DatabaseManagement",
    component: () =>
      import("@/modules/p.adminManagement/views/pages/AdminDataBasePage.vue"),
    meta: { footerIndex: 4, kind: [], depth: 5 },
  },
  {
    path: "/admin-course-level-years",
    name: "Admin-course-level-years",
    component: () =>
      import(
        "@/modules/p.adminManagement/views/pages/AdminCourseYearLevelPage.vue"
      ),
    meta: { footerIndex: 4, kind: [], depth: 5 },
  },
  {
    path: "/admin-academic-years",
    name: "admin-academic-years",
    component: () =>
      import(
        "@/modules/p.adminManagement/views/pages/AdminAcademicYearsPage.vue"
      ),
    meta: { footerIndex: 4, kind: [], depth: 5 },
  },
  {
    path: "/admin-course-curators",
    name: "AdminCourseCurator",
    component: () =>
      import("@/modules/p.adminManagement/views/pages/AdminCourseCurator.vue"),
    meta: { footerIndex: 4, kind: [], depth: 5 },
  },
  {
    path: "/admin-course-uuid-download",
    name: "admin-course-uuid-download",
    component: () =>
      import(
        "@/modules/p.adminManagement/views/pages/AdminCourseDownloadPage.vue"
      ),
    meta: { footerIndex: 4, kind: [], depth: 5 },
  },
  {
    path: "/admin-school-uuid-download",
    name: "admin-school-uuid-download",
    component: () =>
      import(
        "@/modules/p.adminManagement/views/pages/AdminSchoolsDownloadPage.vue"
      ),
    meta: { footerIndex: 4, kind: [], depth: 5 },
  },
  {
    path: "/admin-create-groups",
    name: "admin-create-groups",
    component: () =>
      import(
        "@/modules/p.adminManagement/views/pages/AdminCreateGroupsPage.vue"
      ),
    meta: { footerIndex: 4, kind: [], depth: 5 },
  },
  {
    path: "/admin-create-teacher-account",
    name: "admin-create-teacher-account",
    component: () =>
      import(
        "@/modules/p.adminManagement/views/pages/AdminCreateTeacherAccountPage.vue"
      ),
    meta: { footerIndex: 4, kind: [], depth: 6 },
  },
  {
    path: "/ranking-ebooks",
    name: "ranking-ebooks",
    component: () => import("@/modules/n.ebooks/views/pages/EbookMenuPage.vue"),
    meta: { footerIndex: 4, kind: [], depth: 5 },
  },
  {
    path: "/survey-creation",
    name: "survey-creation",
    component: () => import("@/modules/y.extra/views/pages/SurveyCreation.vue"),
  },
  {
    path: "/survey-creation-questions",
    name: "survey-creation-questions",
    component: () =>
      import("@/modules/y.extra/views/pages/SurveyCreationQuestions.vue"),
  },
  {
    path: "/user-survey-answer",
    name: "user-survey-answer",
    component: () => import("@/modules/y.extra/views/pages/SurveyList.vue"),
  },
  {
    path: "/survey-answer",
    name: "survey-answer",
    component: () => import("@/modules/y.extra/views/pages/SurveyResponse.vue"),
  },
  {
    path: "/survey-answer-start/:survey",
    name: "survey-answer-start",
    component: () => import("@/modules/y.extra/views/pages/SurveyResponse.vue"),
    beforeEnter: async (to) => {
      const uuid = to.params.survey as string;

      const userSurvey = useUserSurveyAnswerStore();
      if (userSurvey.survey.uuid !== uuid) {
        await userSurvey.startSurveyFromRouter(uuid);
        userSurvey.fromStart = true;
      }

      return {
        name: "survey-answer",
        replace: true,
        query: { ...to.query, showInfoModal: "1" },
      };
    },
  },
  {
    path: "/survey-progress-report",
    name: "survey-progress-report",
    component: () => import("@/modules/y.extra/views/pages/SurveyReport.vue"),
  },
  {
    path: "/surveys-answers-management",
    name: "surveys-answers-management",
    component: () =>
      import("@/modules/y.extra/views/pages/SurveysAnswerManagement.vue"),
  },
  {
    path: "/solve-worksheets-live",
    name: "solve-worksheets-live",
    component: () =>
      import("@/modules/3.learn/views/pages/ClassWorksheetLive.vue"),
  },
  {
    path: "/chat-messages",
    name: "chat-messages",
    component: () =>
      import("@/modules/3.learn/views/pages/ChatMessagesPage.vue"),
    meta: { depth: 3 },
  },
  {
    path: "/private-chat-messages",
    name: "private-chat-messages",
    component: () =>
      import("@/modules/3.learn/views/pages/PrivateChatMessagesPage.vue"),
    meta: { depth: 3 },
  },
  {
    path: "/private-chat-messages-teacher",
    name: "private-chat-messages-teacher",
    component: () =>
      import(
        "@/modules/3.learn/views/pages/PrivateChatMessagesTeacherPage.vue"
      ),
    meta: { depth: 3 },
  },
  {
    path: "/teacher-roles",
    name: "teacher-roles",
    component: () =>
      import("@/modules/p.adminManagement/views/pages/TeacherRoles.vue"),
  },
  {
    path: "/user-milage-roles",
    name: "user-milage-roles",
    component: () =>
      import("@/modules/p.adminManagement/views/pages/UserMilageRoles.vue"),
  },

  {
    path: "/author-tests-exams",
    name: "author-tests-exams",
    component: () =>
      import("@/modules/l.exams/views/pages/AuthorTestsExams.vue"),
  },

  {
    path: "/create-tests-exams",
    name: "create-tests-exams",
    component: () =>
      import("@/modules/l.exams/views/pages/CreateTestsExams.vue"),
  },

  {
    path: "/create-update-tests-exams",
    name: "create-update-tests-exams",
    component: () =>
      import("@/modules/l.exams/views/pages/CreateUpdateTestsExams.vue"),
  },

  {
    path: "/teacher-tests-exams-appointments",
    name: "teacher-tests-exams-appointments",
    component: () =>
      import("@/modules/l.exams/views/pages/ExamsAppointments.vue"),
  },

  {
    path: "/teacher-schedule-test-exam",
    name: "teacher-schedule-test-exam",
    component: () =>
      import("@/modules/l.exams/views/pages/ScheduleTestExam.vue"),
  },

  {
    path: "/student-test-exam-solve",
    name: "student-test-exam-solve",
    component: () =>
      import("@/modules/l.exams/views/pages/StudentSolveTestExam.vue"),
  },

  {
    path: "/student-activity-test",
    name: "student-activity-test",
    component: () =>
      import("@/modules/l.exams/views/pages/StudentActivities.vue"),
  },

  {
    path: "/exam-take-photos/:maxNumFiles?/:maxSizeMBytes?/:maxDuration?",
    name: "ExamTakePhotos",
    component: () =>
      import("@/modules/l.exams/views/pages/PhotoMediaPageExam.vue"),
    meta: { depth: 4 },
  },

  {
    path: "/exam-take-photos-ios/:maxNumFiles?/:maxSizeMBytes?/:maxDuration?",
    name: "ExamTakePhotosIos",
    component: () =>
      import("@/modules/l.exams/views/pages/PhotoMediaPageExamIos.vue"),
    meta: { depth: 4 },
  },

  {
    path: "/exam-take-photos-ios/:maxNumFiles?/:maxSizeMBytes?/:maxDuration?",
    name: "ExamTakePhotosIos",
    component: () =>
      import("@/modules/l.exams/views/pages/PhotoMediaPageExamIos.vue"),
    meta: { depth: 4 },
  },

  {
    path: "/exam-take-pdfs/:maxNumFiles?/:maxSizeMBytes?/:maxDuration?",
    name: "ExamTakePdfs",
    component: () =>
      import("@/modules/l.exams/views/pages/ExamPdfMediaPage.vue"),
    meta: { depth: 4 },
  },

  {
    path: "/exam-take-videos/:maxNumFiles?/:maxSizeMBytes?/:maxDuration?",
    name: "ExamTakeVideos",
    component: () =>
      import("@/modules/l.exams/views/pages/ExamVideoMediaPage.vue"),
    meta: { depth: 4 },
  },

  {
    path: "/exam-take-audios/:maxNumFiles?/:maxSizeMBytes?/:maxDuration?",
    name: "ExamTakeAudios",
    component: () =>
      import("@/modules/l.exams/views/pages/ExamAudioMediaPage.vue"),
    meta: { depth: 4 },
  },

  {
    path: "/train-take-photos/:maxNumFiles?/:maxSizeMBytes?/:maxDuration?",
    name: "TrainTakePhotos",
    component: () =>
      import("@/modules/l.exams/views/pages/PhotoMediaPageExam.vue"),
    meta: { depth: 4 },
  },

  {
    path: "/train-take-photos-ios/:maxNumFiles?/:maxSizeMBytes?/:maxDuration?",
    name: "TrainTakePhotosIos",
    component: () =>
      import("@/modules/l.exams/views/pages/PhotoMediaPageExamIos.vue"),
    meta: { depth: 4 },
  },

  {
    path: "/train-take-photos-ios/:maxNumFiles?/:maxSizeMBytes?/:maxDuration?",
    name: "TrainTakePhotosIos",
    component: () =>
      import("@/modules/l.exams/views/pages/PhotoMediaPageExamIos.vue"),
    meta: { depth: 4 },
  },

  {
    path: "/train-take-pdfs/:maxNumFiles?/:maxSizeMBytes?/:maxDuration?",
    name: "TrainTakePdfs",
    component: () =>
      import("@/modules/l.exams/views/pages/ExamPdfMediaPage.vue"),
    meta: { depth: 4 },
  },

  {
    path: "/train-take-videos/:maxNumFiles?/:maxSizeMBytes?/:maxDuration?",
    name: "TrainTakeVideos",
    component: () =>
      import("@/modules/l.exams/views/pages/ExamVideoMediaPage.vue"),
    meta: { depth: 4 },
  },

  {
    path: "/train-take-audios/:maxNumFiles?/:maxSizeMBytes?/:maxDuration?",
    name: "TrainTakeAudios",
    component: () =>
      import("@/modules/l.exams/views/pages/ExamAudioMediaPage.vue"),
    meta: { depth: 4 },
  },

  {
    path: "/teacher-test-exam-assessment-list",
    name: "teacher-test-exam-assessment-list",
    component: () =>
      import("@/modules/l.exams/views/pages/TeacherTestExamAssessmentList.vue"),
  },

  {
    path: "/worksheet-curation",
    name: "worksheet-curation",
    component: () =>
      import("@/modules/p.adminManagement/views/pages/CurationPage.vue"),
  },

  {
    path: "/student-test-exam-waiting-room",
    name: "student-test-exam-waiting-room",
    component: () =>
      import("@/modules/l.exams/views/pages/WaitingRoomPage.vue"),
  },

  {
    path: "/activity-train-start",
    name: "activity-train-start",
    component: () =>
      import(
        "@/modules/q.studentActivities/views/pages/ActivityTrainStart.vue"
      ),
  },

  {
    path: "/activity-creation",
    name: "activity-creation",
    component: () =>
      import("@/modules/q.studentActivities/views/pages/ActivityCreation.vue"),
  },

  {
    path: "/student-activity-train-solve",
    name: "student-activity-train-solve",
    component: () =>
      import("@/modules/q.studentActivities/views/pages/ActivitySolve.vue"),
  },

  {
    path: "/test-exam-live",
    name: "test-exam-live",
    component: () => import("@/modules/l.exams/views/pages/TestExamLive.vue"),
  },

  {
    path: "/formative-worksheets-teacher-start",
    name: "formative-worksheets-teacher-start",
    component: () =>
      import(
        "@/modules/q.studentActivities/views/pages/FormativeActivities.vue"
      ),
  },

  {
    path: "/formative-create-worksheets",
    name: "formative-create-worksheets",
    component: () =>
      import(
        "@/modules/q.studentActivities/views/pages/FormativeCreateWorksheets.vue"
      ),
  },

  {
    path: "/formative-schedule-appointments",
    name: "formative-schedule-appointments",
    component: () =>
      import(
        "@/modules/q.studentActivities/views/pages/FormativeAppointments.vue"
      ),
  },

  {
    path: "/create-update-teacher-schedule-formative",
    name: "create-update-teacher-schedule-formative",
    component: () =>
      import("@/modules/q.studentActivities/views/pages/ScheduleFormative.vue"),
  },

  {
    path: "/formative-worksheets-student-start",
    name: "formative-worksheets-student-start",
    component: () =>
      import(
        "@/modules/q.studentActivities/views/pages/StudentActivitiesFormative.vue"
      ),
  },

  {
    path: "/formative-assessment-list",
    name: "formative-assessment-list",
    component: () =>
      import(
        "@/modules/q.studentActivities/views/pages/TeacherFormativeAssessmentList.vue"
      ),
  },

  {
    path: "/formative-activity-solve",
    name: "formative-activity-solve",
    component: () =>
      import(
        "@/modules/q.studentActivities/views/pages/FormativeActivitieSolve.vue"
      ),
  },

  {
    path: "/formative-take-photos/:maxNumFiles?/:maxSizeMBytes?/:maxDuration?",
    name: "FormativeTakePhotos",
    component: () =>
      import(
        "@/modules/q.studentActivities/views/pages/FormativePhotoMediaPage.vue"
      ),
    meta: { depth: 4 },
  },

  {
    path: "/formative-take-photos-ios/:maxNumFiles?/:maxSizeMBytes?/:maxDuration?",
    name: "FormativeTakePhotosIos",
    component: () =>
      import(
        "@/modules/q.studentActivities/views/pages/FormativePhotoMediaPageIos.vue"
      ),
    meta: { depth: 4 },
  },

  {
    path: "/formative-take-pdfs/:maxNumFiles?/:maxSizeMBytes?/:maxDuration?",
    name: "FormativeTakePdfs",
    component: () =>
      import(
        "@/modules/q.studentActivities/views/pages/FormativePdfMediaPage.vue"
      ),
    meta: { depth: 4 },
  },

  {
    path: "/formative-take-videos/:maxNumFiles?/:maxSizeMBytes?/:maxDuration?",
    name: "FormativeTakeVideos",
    component: () =>
      import(
        "@/modules/q.studentActivities/views/pages/FormativeVideoMediaPage.vue"
      ),
    meta: { depth: 4 },
  },

  {
    path: "/formative-take-audios/:maxNumFiles?/:maxSizeMBytes?/:maxDuration?",
    name: "FormativeTakeAudios",
    component: () =>
      import(
        "@/modules/q.studentActivities/views/pages/FormativeAudioMediaPage.vue"
      ),
    meta: { depth: 4 },
  },

  {
    path: "/student-formative-waiting-room",
    name: "student-formative-waiting-room",
    component: () =>
      import(
        "@/modules/q.studentActivities/views/pages/StudentFormativeWaitingRoom.vue"
      ),
  },

  {
    path: "/formative-live",
    name: "formative-live",
    component: () =>
      import("@/modules/q.studentActivities/views/pages/FormativeLive.vue"),
  },

  {
    path: "/select-worksheet-mode",
    name: "select-worksheet-mode",
    component: () =>
      import("@/modules/l.exams/views/pages/SelectWorksheetMode.vue"),
  },

  {
    path: "/select-worksheet-for-exam",
    name: "select-worksheet-for-exam",
    component: () =>
      import("@/modules/l.exams/views/pages/SelectWorksheetForExam.vue"),
  },

  {
    path: "/select-random-problems",
    name: "select-random-problems",
    component: () =>
      import("@/modules/l.exams/views/pages/SelectRandomProblems.vue"),
  },

  {
    path: "/homework-teacher-start",
    name: "homework-teacher-start ",
    component: () =>
      import(
        "@/modules/q.studentActivities/views/pages/HomeworkTeacherStart.vue"
      ),
  },

  {
    path: "/workout-teacher-start",
    name: "workout-teacher-start",
    component: () =>
      import(
        "@/modules/q.studentActivities/views/pages/TeacherTrainStudents.vue"
      ),
  },

  {
    path: "/teacher-homework-create-worksheets",
    name: "teacher-homework-create-worksheets",
    component: () =>
      import("@/modules/q.studentActivities/views/pages/HomeworkCreate.vue"),
  },

  {
    path: "/teacher-homework-schedule-appointments",
    name: "teacher-homework-schedule-appointments",
    component: () =>
      import(
        "@/modules/q.studentActivities/views/pages/HomeworkAppointments.vue"
      ),
  },

  {
    path: "/create-update-teacher-schedule-homework",
    name: "create-update-teacher-schedule-homework",
    component: () =>
      import("@/modules/q.studentActivities/views/pages/HomeworkSchedule.vue"),
  },

  {
    path: "/homework-assessment-list",
    name: "homework-assessment-list",
    component: () =>
      import(
        "@/modules/q.studentActivities/views/pages/HomeworkAssessmentList.vue"
      ),
  },

  {
    path: "/activity-homework-student-start",
    name: "activity-homework-student-start",
    component: () =>
      import("@/modules/q.studentActivities/views/pages/HomeworkStudents.vue"),
  },

  {
    path: "/metrics-milage",
    name: "metrics-milage",
    component: () =>
      import("@/modules/x.reports/views/pages/MetricsMilage.vue"),
  },

  {
    path: "/metrics-user-logins",
    name: "metrics-user-logins",
    component: () =>
      import("@/modules/x.reports/views/pages/UserLogsMetrics.vue"),
  },

  {
    path: "/users",
    name: "users",
    component: () =>
      import("@/modules/x.reports/views/pages/UserLogsMetrics.vue"),
  },

  {
    path: "/milage-tutorials",
    name: "milage-tutorials",
    component: () =>
      import("@/modules/y.extra/views/pages/MilageTutorials.vue"),
  },
];

const router = createRouter({
  //history: createWebHistory(process.env.BASE_URL),
  history: createWebHistory(process.env.BASE_URL),
  routes,
  scrollBehavior(to) {
    if (to.hash) {
      return { el: to.hash, behavior: "smooth" };
    }
    return { top: 0 };
  },
});

class CustomRouter implements Router {
  routeHistory = ref<RouteLocationNormalizedLoaded[]>([]);

  _router: Router;
  public forceNextRoute = false;

  constructor(router: Router) {
    this._router = router;

    this.addRoute = this._router.addRoute.bind(this._router);

    this.afterEach = this._router.afterEach.bind(this._router);
    this.beforeEach = this._router.beforeEach.bind(this._router);
    this.beforeResolve = this._router.beforeResolve.bind(this._router);
    this.clearRoutes = this.clearRoutes.bind(router);

    this.forward = this._router.forward.bind(this._router);
    this.getRoutes = this._router.getRoutes.bind(this._router);
    this.go = this._router.go.bind(this._router);
    this.hasRoute = this._router.hasRoute.bind(this._router);
    this.install = this._router.install.bind(this._router);
    this.isReady = this._router.isReady.bind(this._router);

    this.onError = this._router.onError.bind(this._router);

    //this.push = this._router.push.bind(this._router)
    this.removeRoute = this._router.removeRoute.bind(this._router);
    this.replace = this._router.replace.bind(this._router);
    this.resolve = this._router.resolve.bind(this._router);
  }

  addRoute = router.addRoute.bind(router);
  afterEach = router.afterEach;
  beforeEach = router.beforeEach;
  beforeResolve = router.beforeResolve;

  currentRoute = router.currentRoute;
  forward = router.forward;
  getRoutes = router.getRoutes;
  go = router.go;
  hasRoute = router.hasRoute;
  install = router.install;
  isReady = router.isReady;
  listening = router.listening;
  onError = router.onError;
  options = router.options;
  push = (to: RouteLocationRaw) => {
    this.forceNextRoute = true;
    return this._router.push(to);
  };
  removeRoute = router.removeRoute;
  replace = (to: RouteLocationRaw) => {
    this.forceNextRoute = true;
    return router.push(to);
  };
  resolve = router.resolve;
  clearRoutes = () => {
    const allRoutes = this.getRoutes();
    allRoutes.forEach((route) => {
      if (route.name) {
        this.removeRoute(route.name);
      }
    });
  };

  back = () => {
    // get the current route
    const currentRoute = router.currentRoute.value;
    const meta = currentRoute.meta as routeMetaData;
    // get the current depth
    const currentDepth = meta.depth;

    let previousRoute: null | RouteLocationNormalizedLoaded = null;
    for (let i = this.routeHistory.value.length - 2; i >= 0; i--) {
      const meta = this.routeHistory.value[i].meta as routeMetaData;

      if (currentDepth === undefined && meta.depth !== undefined) {
        previousRoute = this.routeHistory.value[i];
        break;
      }

      if (currentDepth && meta && meta.depth && meta.depth < currentDepth) {
        previousRoute = this.routeHistory.value[i];
        break;
      }
    }

    if (previousRoute) {
      this.forceNextRoute = true;
      this.includeRouteInTree(previousRoute);
      router.push(previousRoute.fullPath);
    } else {
      //console.error("No hierarchically higher route found in the history.");
      this._router.back(); // fallback logic
    }
  };
  private includeRouteInTree(newRoute: RouteLocationNormalizedLoaded) {
    const newRouteDepth = (newRoute.meta as routeMetaData).depth;
    console.log("newRouteDepth", newRouteDepth);
    if (typeof newRouteDepth === "number") {
      console.log("routeHistory before filtering", this.routeHistory.value);
      this.routeHistory.value = this.routeHistory.value.filter(
        (r) =>
          r.meta.depth !== undefined &&
          (r.meta.depth as number) < newRouteDepth,
      );
      console.log("routeHistory after filtering", this.routeHistory.value);
    }

    this.routeHistory.value.push(newRoute);
  }
}

// const customRouter = new CustomRouter(router)
// console.log("custom router", customRouter)

// router.beforeEach((to, from) => {
//   // redirecto to login when user type the URL
//   if (!tokenStore.isAuth && to.fullPath != "/login") {
//     router.replace("/login");
//     return true;
//   }
//   //   console.log("ROUTER 1 FROM-TO", from, to);
//   //   if (!tokenStore.isAuth) {
//   //     if (to.path !== "/login") return false;
//   //   }

//   if (to.path === "/") {
//     console.log("FOUND");
//   }
// });
router.beforeEach((to) => {
  const isPublic = to.matched.some((r) => r.meta?.public);
  if (!tokenStore.isAuth && !isPublic) {
    return { path: "/login", replace: true };
  }
  return true;
});

export default router;

// armazena a última rota
let previousRoute: RouteLocationNormalizedLoaded | null = null;

// sempre que qualquer navegação for disparada, atualiza a previousRoute
router.beforeEach((to, from, next) => {
  console.log("ROUTER 2 FROM-TO", from, to);
  previousRoute = from;
  next();
});

// exporta pra usar depois
export function getPreviousRoute() {
  return previousRoute;
}

/*
  addRouter = router.addRoute;
  afterEach = router.afterEach
  beforeEach = router.beforeEach
  beforeResolve = router.beforeResolve
  currentRoute = router.currentRoute
  forward = router.forward
  getRoutes = router.getRoutes
  go = router.go
  hasRoute = router.hasRoute
  install = router.install
  isReady = router.isReady
  listening = router.listening
  onError = router.onError
  options = router.options;
  push = router.push.bind(router)
  removeRoute = router.removeRoute;
  replace = router.replace
  resolve = router.resolve

*/
